import React, { useState } from "react";
import { Button, Card, Col, Dropdown, message, Modal, Popconfirm, Progress, Row, Select, Statistic, Table } from "antd";
import { useFirestore, useFirestoreConnect } from "react-redux-firebase";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  FIRESTORE_ANALYSIS_RFM_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_USERS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { useTranslation } from "react-i18next";
import { DeleteOutlined, EyeOutlined, MoreOutlined, PlayCircleOutlined } from "@ant-design/icons";

const RFMDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState("");
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("6months");
  const [deletingAnalysis, setDeletingAnalysis] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const firestore = useFirestore();

  // Connect to Firestore to get all RFM analyses
  useFirestoreConnect([{ collection: FIRESTORE_ANALYSIS_RFM_TABLE, orderBy: ["timestamp", "desc"] }]);

  const analyses = useSelector((state) => state.firestoreReducer.ordered.analysisRfm || []);

  // Safety limits
  const BATCH_SIZE = 500;

  // Period options
  const periodOptions = [
    { value: "6months", label: t("6_months"), months: 6 },
    { value: "1year", label: t("1_year"), months: 12 },
    { value: "1.5years", label: t("1_5_years"), months: 18 },
    { value: "2years", label: t("2_years"), months: 24 },
    // { value: "alltime", label: t("all_time"), months: null },
  ];

  const getPeriodLabel = (period) => {
    const option = periodOptions.find((opt) => opt.value === period);
    return option ? option.label : period;
  };

  const getPeriodMonths = (period) => {
    const option = periodOptions.find((opt) => opt.value === period);
    return option ? option.months : null;
  };

  // Safely fetch orders in batches
  const fetchOrdersData = async (period) => {
    setLoadingStatus(t("fetching_orders_data"));
    setLoadingProgress(10);

    try {
      let ordersQuery = firestore.collection(FIRESTORE_ORDERS_TABLE);

      // Apply date filter if not "all time"
      if (period !== "alltime") {
        const months = getPeriodMonths(period);
        const periodAgo = dayjs().subtract(months, "months").unix();
        ordersQuery = ordersQuery.where("createdAt", ">=", periodAgo);
      }

      const ordersSnapshot = await ordersQuery.get();

      setLoadingProgress(50);
      setLoadingStatus(t("processing_orders"));

      const allOrders = [];
      ordersSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.orderStatus !== "CREATED") {
          allOrders.push({ id: doc.id, ...data });
        }
      });

      console.log(`Fetched ${allOrders.length} orders for period: ${period}`);
      return allOrders;
    } catch (error) {
      console.error("Error fetching orders:", error);
      throw error;
    }
  };

  // Safely fetch users data
  const fetchUsersData = async () => {
    setLoadingStatus(t("fetching_users_data"));
    setLoadingProgress(60);

    try {
      const usersQuery = await firestore.collection(FIRESTORE_USERS_TABLE).where("role", "==", "webshopUser").get();

      setLoadingProgress(70);
      setLoadingStatus(t("processing_users_data"));

      const users = [];
      usersQuery.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() });
      });

      console.log(`Fetched ${users.length} webshop users`);
      return users;
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  };

  const runNewAnalysis = async () => {
    console.log(`Starting new RFM analysis for period: ${selectedPeriod}...`);
    setLoading(true);
    setLoadingProgress(0);
    setShowPeriodModal(false);

    try {
      // Step 1: Fetch data safely
      const orders = await fetchOrdersData(selectedPeriod);
      const users = await fetchUsersData();

      setLoadingStatus(t("calculating_rfm_metrics"));
      setLoadingProgress(80);

      // Step 2: Process data in batches
      const processInBatches = async (orders, users) => {
        const customerOrderMap = {};

        for (let i = 0; i < orders.length; i += BATCH_SIZE) {
          const batch = orders.slice(i, i + BATCH_SIZE);

          batch.forEach((order) => {
            const email = order.customer?.email;
            if (email && order.customer?.uid !== "0") {
              if (!customerOrderMap[email]) {
                customerOrderMap[email] = [];
              }
              customerOrderMap[email].push(order);
            }
          });

          if (i % (BATCH_SIZE * 2) === 0) {
            await new Promise((resolve) => setTimeout(resolve, 10));
          }
        }

        return customerOrderMap;
      };

      const customerOrderMap = await processInBatches(orders, users);
      const now = dayjs().unix();

      setLoadingStatus(t("finalizing_calculations"));
      setLoadingProgress(90);

      // Calculate RFM for each customer
      const rfmResults = Object.entries(customerOrderMap).map(([email, customerOrders]) => {
        const userData = users.find((user) => user.email === email);

        let lastOrderTime;
        if (userData && userData.lastOrderTimestamp) {
          lastOrderTime = userData.lastOrderTimestamp;
        } else {
          lastOrderTime = Math.max(...customerOrders.map((o) => o.createdAt));
        }
        const recency = Math.floor((now - lastOrderTime) / (24 * 60 * 60));

        // Calculate frequency based on period
        const periodMonths =
          getPeriodMonths(selectedPeriod) ||
          (selectedPeriod === "alltime"
            ? Math.max(1, Math.ceil((now - Math.min(...customerOrders.map((o) => o.createdAt))) / (30 * 24 * 60 * 60)))
            : 6);
        const frequency = (customerOrders.length / periodMonths).toFixed(2);

        const totalSpent = customerOrders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
        const monetary = (totalSpent / customerOrders.length).toFixed(2);

        return {
          email,
          recency,
          frequency: parseFloat(frequency),
          monetary: parseFloat(monetary),
          totalOrders: customerOrders.length,
          totalSpent,
        };
      });

      // Calculate distributions
      const createDistribution = (data, key, buckets = 20) => {
        const values = data.map((item) => item[key]).sort((a, b) => a - b);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const bucketSize = (max - min) / buckets;

        return Array.from({ length: buckets }, (_, i) => {
          const bucketMin = min + i * bucketSize;
          const bucketMax = min + (i + 1) * bucketSize;
          const count = values.filter((v) => v >= bucketMin && v < bucketMax).length;

          let range;
          if (key === "recency") {
            range = `${Math.floor(bucketMin)}-${Math.floor(bucketMax)} ${t("days")}`;
          } else if (key === "frequency") {
            range = `${bucketMin.toFixed(1)}-${bucketMax.toFixed(1)}/${t("month")}`;
          } else {
            range = `€${bucketMin.toFixed(0)}-€${bucketMax.toFixed(0)}`;
          }

          return { range, count, bucketMin };
        });
      };

      const distributions = {
        recency: createDistribution(rfmResults, "recency"),
        frequency: createDistribution(rfmResults, "frequency"),
        monetary: createDistribution(rfmResults, "monetary"),
      };

      // Calculate percentiles
      const calculatePercentiles = (data, key) => {
        const sorted = data.map((item) => item[key]).sort((a, b) => a - b);
        return {
          p20: sorted[Math.floor(sorted.length * 0.2)],
          p40: sorted[Math.floor(sorted.length * 0.4)],
          p60: sorted[Math.floor(sorted.length * 0.6)],
          p80: sorted[Math.floor(sorted.length * 0.8)],
          min: sorted[0],
          max: sorted[sorted.length - 1],
        };
      };

      const percentiles = {
        recency: calculatePercentiles(rfmResults, "recency"),
        frequency: calculatePercentiles(rfmResults, "frequency"),
        monetary: calculatePercentiles(rfmResults, "monetary"),
      };

      // Calculate order stats (guest vs customer orders)
      const customerOrders = orders.filter((order) => order.customer?.uid !== "0");
      const guestOrders = orders.filter((order) => order.customer?.uid === "0");

      const customerPercentage = orders.length > 0 ? ((customerOrders.length / orders.length) * 100).toFixed(1) : "0";
      const guestPercentage = orders.length > 0 ? ((guestOrders.length / orders.length) * 100).toFixed(1) : "0";

      const orderStats = {
        customerOrders: customerOrders.length,
        guestOrders: guestOrders.length,
        customerPercentage,
        guestPercentage,
      };

      // Prepare data for Firestore
      const analysisData = {
        timestamp: dayjs().unix(),
        name: dayjs().format("MMMM D, YYYY HH:mm"),
        period: selectedPeriod,
        periodLabel: getPeriodLabel(selectedPeriod),
        totalCustomers: rfmResults.length,
        totalActiveCustomers: users.length,
        totalOrdersAnalyzed: orders.length,
        activationRate: ((rfmResults.length / users.length) * 100).toFixed(1),
        orderStats,
        percentiles,
        distributions,
      };

      setLoadingStatus(t("saving_analysis"));
      setLoadingProgress(95);

      // Save to Firestore
      const docRef = await firestore.collection(FIRESTORE_ANALYSIS_RFM_TABLE).add(analysisData);

      setLoadingProgress(100);
      setLoadingStatus(t("analysis_complete"));

      message.success(t("analysis_saved_successfully"));

      // Navigate to the detail view
      setTimeout(() => {
        navigate(`/cms/reports/rfm/${docRef.id}`);
      }, 1000);
    } catch (error) {
      console.error("Error running analysis:", error);
      setLoadingStatus(`Error: ${error.message}`);
      message.error(t("analysis_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleStartAnalysis = () => {
    setShowPeriodModal(true);
  };

  const handlePeriodConfirm = () => {
    runNewAnalysis();
  };

  const handleDeleteAnalysis = async (analysisId) => {
    try {
      setDeletingAnalysis(analysisId);
      await firestore.collection(FIRESTORE_ANALYSIS_RFM_TABLE).doc(analysisId).delete();
      message.success(t("analysis_deleted_successfully"));
    } catch (error) {
      console.error("Error deleting analysis:", error);
      message.error(t("error_deleting_analysis"));
    } finally {
      setDeletingAnalysis(null);
    }
  };

  const columns = [
    {
      title: t("analysis_name"),
      dataIndex: "name",
      render: (name, record) => (
        <div>
          <div>RFM Analysis - {name}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {t("period")}: {record.periodLabel || t("6_months")}
          </div>
        </div>
      ),
      width: 300,
    },
    {
      title: t("date"),
      dataIndex: "timestamp",
      render: (timestamp) => dayjs.unix(timestamp).format("MMMM D, YYYY HH:mm"),
      width: 200,
    },
    {
      title: t("period"),
      dataIndex: "periodLabel",
      render: (periodLabel) => periodLabel || t("6_months"),
      width: 120,
    },
    {
      title: t("customers_analyzed"),
      dataIndex: "totalCustomers",
      width: 150,
    },
    {
      title: t("activation_rate"),
      dataIndex: "activationRate",
      render: (rate) => `${rate}%`,
      width: 150,
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button type="primary" icon={<EyeOutlined />} onClick={() => navigate(`/cms/reports/rfm/${record.id}`)}>
            {t("view")}
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: "delete",
                  label: (
                    <Popconfirm
                      title={t("delete_analysis")}
                      description={t("are_you_sure_delete_analysis")}
                      onConfirm={() => handleDeleteAnalysis(record.id)}
                      okText={t("yes")}
                      cancelText={t("no")}
                      okButtonProps={{ danger: true }}
                    >
                      <div style={{ color: "#ff4d4f" }}>
                        <DeleteOutlined /> {t("delete")}
                      </div>
                    </Popconfirm>
                  ),
                },
              ],
            }}
            trigger={["click"]}
          >
            <Button
              icon={<MoreOutlined />}
              loading={deletingAnalysis === record.id}
              disabled={deletingAnalysis === record.id}
            />
          </Dropdown>
        </div>
      ),
      width: 200,
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      {/* Loading overlay */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card style={{ minWidth: 400 }}>
            <div style={{ textAlign: "center" }}>
              <Progress percent={loadingProgress} />
              <p style={{ marginTop: "10px" }}>{loadingStatus}</p>
              <p className="text-muted">{t("processing_batches", { size: BATCH_SIZE })}</p>
            </div>
          </Card>
        </div>
      )}

      {/* Period Selection Modal */}
      <Modal
        title={t("select_analysis_period")}
        open={showPeriodModal}
        onOk={handlePeriodConfirm}
        onCancel={() => setShowPeriodModal(false)}
        okText={t("start_analysis")}
        cancelText={t("cancel")}
      >
        <div style={{ marginBottom: "16px" }}>
          <p>{t("select_period_description")}</p>
        </div>
        <Select style={{ width: "100%" }} value={selectedPeriod} onChange={setSelectedPeriod} options={periodOptions} />
      </Modal>

      <Row gutter={16} className="mb-4">
        <Col span={16}>
          <h2>{t("rfm_analysis_dashboard")}</h2>
          <p>{t("manage_rfm_analysis")}</p>
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            size="large"
            icon={<PlayCircleOutlined />}
            onClick={handleStartAnalysis}
            loading={loading}
            disabled={loading}
          >
            {loading ? t("analyzing") : t("run_new_analysis")}
          </Button>
        </Col>
      </Row>

      {/* Summary Stats */}
      {analyses.length > 0 && (
        <Row gutter={16} className="mb-4">
          <Col span={6}>
            <Card>
              <Statistic title={t("total_analyses")} value={analyses.length} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title={t("latest_analysis")} value={dayjs.unix(analyses[0]?.timestamp).format("MMM D")} />
            </Card>
          </Col>
        </Row>
      )}

      {/* Analyses Table */}
      <Card title={t("analysis_history")}>
        {analyses.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <p>{t("no_analyses_yet")}</p>
            <p className="text-muted">{t("run_first_analysis_to_get_started")}</p>
          </div>
        ) : (
          <Table
            showSorterTooltip={false}
            columns={columns}
            dataSource={analyses}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 800 }}
          />
        )}
      </Card>
    </div>
  );
};

export default RFMDashboard;
